/* eslint-disable react/prop-types */
import { useContext, useState, useEffect } from 'react';
import Moment from 'moment';
import {
  faCircleQuestion,
  faDownload,
} from '@fortawesome/free-solid-svg-icons';
import ClientService from '../../../../../Services/ClientService';
import KYCService from '../../../../../Services/KYCService';
import ScreeningService from '../../../../../Services/ScreeningService';
import Module from '../Module';
import DataPanel from '../../../../../Components/DataPanel/DataPanel';
import LoadingBar from '../../../../../Components/LoadingBar/LoadingBar';
import KYCView from './KYCView';
import XpektorButton from '../../../../../Components/XpektorButton/XpektorButton';
import ScreeningResultModal from '../../../Modals/ScreeningResultModal';
import TabNav from '../../../../../Components/TabNav/TabNav';
import XpektorSlider from '../../../../../Components/XpektorSlider/XpektorSlider';
import CrimeRecordView from './CrimeRecordView';
import AuthContext from '../../../../../Context/AuthContext';

const gT = (t, key) => t(`company-info-modules.regulatory${key}`);
const gsT = (t, key) => t(`shared${key}`);

export default function Regulatory({ client, onPdfClick, isImpersonating, t }) {
  const [regulatoryInfo, setRegulatoryInfo] = useState({});
  const [payrollData, setPayrollData] = useState([]);
  const [activeDirectors, setActiveDirectors] = useState([]);
  const [inactiveDirectors, setInactiveDirectors] = useState([]);
  const [ongoingMatters, setOngoingMatters] = useState([]);
  const [fetchingRegulatory, setFetchingRegulatory] = useState(false);
  const [regulatoryTab, setRegulatoryTab] = useState(0);

  const [kycStatus, setKYCStatus] = useState({});
  const [screenings, setScreenings] = useState([]);
  const [screeningsFetched, setScreeningsFetched] = useState(false);
  const [screeningInProgress, setScreeningInProgress] = useState(false);
  const [showScreeningResultModal, setShowScreeningResultModal] =
    useState(false);
  const [selectedResult, setSelectedResult] = useState({});
  const [count, setCount] = useState(0);
  const [authState] = useContext(AuthContext);

  const createNewKYCProcess = async () => {
    await KYCService.createKYCState(client.id)
      .then(() => {
        KYCService.getKYCOverview(client.id)
          .then((overviewResponse) => {
            setKYCStatus(overviewResponse.data);
          })
          .catch(() => {});
      })
      .catch();
  };

  const cancelKYCSubmission = async () => {
    await KYCService.cancelKYCSubmission(kycStatus.stateId)
      .then(() => {
        KYCService.getKYCOverview(client.id)
          .then((overviewResponse) => {
            setKYCStatus(overviewResponse.data);
          })
          .catch(() => {});
      })
      .catch(() => {});
  };

  const requestScreening = async () => {
    if (screeningsFetched && !screeningInProgress) {
      setScreeningInProgress(true);
      await ScreeningService.startClientAMLScreening(client.id)
        .then(() => {})
        .catch(() => {
          setScreeningInProgress(false);
        })
        .finally(() => {
          ScreeningService.getClientAMLScreenings(client.id).then(
            (newScreeningsResponse) => {
              setScreenings(newScreeningsResponse.data);
            }
          );
        });
    }
  };

  const getDateYMD = (date) =>
    date ? Moment(date).format('YYYY-MM-DD') : null;

  const getScreeningUpdates = async () => {
    setCount(count + 1);
    await ScreeningService.getClientAMLScreenings(client.id)
      .then((response) => {
        // Find the unfinished screening in CURRENT screenings
        const unfinishedScreening = screenings.find((screening) => {
          return screening.dateFinished === null;
        });

        // Could be the culprit cancelling the polling. Fine to comment out so long as
        // screening is never set to in progress when it shouldn't be.
        if (!unfinishedScreening) {
          // setScreeningInProgress(false);
          return;
        }

        // Check if the unfinished screening exists AND has been completed in the RESPONSE screenings
        const finishedScreening = response.data.find(
          (screening) =>
            screening.id === unfinishedScreening.id &&
            screening.dateFinished !== null
        );

        if (finishedScreening) {
          setScreenings(response.data);
          setScreeningInProgress(false);
        }
      })
      .catch(() => {
        setScreeningInProgress(false);
      });
  };

  useEffect(() => {
    const timer = setTimeout(
      () => screeningInProgress && getScreeningUpdates(),
      5000
    );
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [count, screeningInProgress]);

  useEffect(() => {
    const getData = async (clientId) => {
      setFetchingRegulatory(true);

      await ClientService.getRegulatoryInfo(clientId)
        .then((response) => {
          setRegulatoryInfo(response.data);
          setActiveDirectors(response.data.activeDirectors);
          setInactiveDirectors(response.data.inactiveDirectors);
          setPayrollData(response.data.payrollData);
          setOngoingMatters(response.data.ongoingMatters);
        })
        .catch(() => {
          setRegulatoryInfo(null);
        })
        .finally(() => {
          setFetchingRegulatory(false);
        });
    };
    const getKYCData = async (clientId) => {
      await KYCService.getKYCOverview(clientId)
        .then((response) => {
          setKYCStatus(response.data);
        })
        .catch(() => {
          setKYCStatus(null);
        });
    };
    const getAMLScreenings = async (clientId) => {
      await ScreeningService.getClientAMLScreenings(clientId)
        .then((response) => {
          setScreenings(response.data);
          const unfinishedScreening = response.data.find(
            (screening) => screening.dateFinished === null
          );
          if (!unfinishedScreening) {
            setScreeningInProgress(false);
          } else {
            setScreeningInProgress(true);
          }
        })
        .catch(() => {})
        .finally(() => {
          setScreeningsFetched(true);
        });
    };
    if (Object.keys(client).length !== 0 && client.errorCode === null) {
      getData(client.id).then(() => {
        getKYCData(client.id);
        getAMLScreenings(client.id);
      });
    }
  }, [client]);

  const sortedPayrollData = payrollData.sort(
    (a, b) => new Date(b.payrollDate) - new Date(a.payrollDate)
  );

  return (
    <Module>
      {client.errorCode !== null ? (
        <p className="module-empty-text">{client.errorCode}</p>
      ) : fetchingRegulatory ? (
        <LoadingBar active status={gT(t, '.loading-bar')} relative />
      ) : regulatoryInfo === null ? (
        <p className="module-empty-text">{gT(t, '.module-empty-text')}</p>
      ) : (
        <>
          <Module.Nav>
            <TabNav activeIndex={regulatoryTab} buttonWidth={120}>
              <TabNav.Tab
                title={gT(t, '.tab-title.regulatory')}
                active={regulatoryTab === 0}
                onClick={() => setRegulatoryTab(0)}
                width={120}
              />
              <TabNav.Tab
                title={gT(t, '.tab-title.kyc')}
                active={regulatoryTab === 1}
                onClick={() => setRegulatoryTab(1)}
                width={120}
              />
              <TabNav.Tab
                title={gT(t, '.tab-title.legal-info')}
                active={regulatoryTab === 3}
                onClick={() => setRegulatoryTab(2)}
                width={120}
              />
            </TabNav>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
              <XpektorButton
                icon={faDownload}
                content={gT(t, '.xpektor-btn.save-as-pdf')}
                onClick={() =>
                  onPdfClick(() =>
                    document.getElementById('regulatory-pdf-wrapper')
                  )
                }
              />
            </div>
          </Module.Nav>
          <XpektorSlider activeSlideIndex={regulatoryTab}>
            <XpektorSlider.Slide hidden={regulatoryTab !== 0}>
              <div id="regulatory-pdf-wrapper">
                <Module.DataSection>
                  <Module.DataColumn>
                    <DataPanel title={gT(t, '.data-panel-title.company-info')}>
                      <DataPanel.ValueEntry
                        label={gT(t, '.value-entry.label.company-type')}
                        value={
                          regulatoryInfo?.companyType ??
                          gT(t, '.value-entry.value.missing-info')
                        }
                      />
                      <DataPanel.ValueEntry
                        label={gT(t, '.value-entry.label.company-active')}
                        value={
                          regulatoryInfo
                            ? regulatoryInfo.companyActive === true
                              ? gsT(t, '.labels.yes')
                              : gsT(t, '.labels.no')
                            : gT(t, '.value-entry.value.missing-info')
                        }
                      />
                      <DataPanel.ValueEntry
                        label={gT(t, '.value-entry.label.nr-employees')}
                        value={
                          regulatoryInfo?.nrEmployees ??
                          gT(t, '.value-entry.value.missing-info')
                        }
                      />
                      <DataPanel.ValueEntry
                        label={gT(t, '.value-entry.label.sni-description')}
                        value={
                          regulatoryInfo?.sni || regulatoryInfo?.sniDescription
                            ? `${regulatoryInfo.sni ?? ''}${
                                regulatoryInfo?.sni &&
                                regulatoryInfo.sniDescription
                                  ? ','
                                  : ''
                              } ${regulatoryInfo.sniDescription ?? ''}`
                            : gT(t, '.value-entry.value.missing-info')
                        }
                      />
                      <DataPanel.ValueEntry
                        label={gT(t, '.value-entry.label.risk-branch')}
                        value={
                          regulatoryInfo?.riskBranch !== null
                            ? regulatoryInfo.riskBranch === true
                              ? gsT(t, '.labels.yes')
                              : gsT(t, '.labels.no')
                            : gT(t, '.value-entry.value.missing-info')
                        }
                        error={regulatoryInfo?.riskBranch === true}
                      />
                      <DataPanel.ValueEntry
                        label={gT(t, '.value-entry.label.date-address-changed')}
                        value={
                          regulatoryInfo?.dateAddressChanged
                            ? Moment(regulatoryInfo.dateAddressChanged).format(
                                'YYYY-MM-DD'
                              )
                            : gT(t, '.value-entry.value.missing-info')
                        }
                      />
                      <DataPanel.ValueEntry
                        label={gT(t, '.value-entry.label.old-name')}
                        value={
                          regulatoryInfo?.oldName ??
                          gT(t, '.value-entry.value.missing-info')
                        }
                      />
                      {regulatoryInfo?.companyType === 'EF' && (
                        <DataPanel.ValueEntry
                          label={gT(t, '.value-entry.label.non-ltd-owner-name')}
                          value={
                            regulatoryInfo?.nonLtdOwnerName &&
                            regulatoryInfo.nonLtdOwnerName.length > 0
                              ? regulatoryInfo.nonLtdOwnerName
                              : gT(t, '.value-entry.value.missing-info')
                          }
                        />
                      )}
                      <DataPanel.ValueEntry
                        label={gT(t, '.value-entry.label.group-mother-name')}
                        value={
                          regulatoryInfo?.groupMotherName &&
                          regulatoryInfo.groupMotherName.length > 0
                            ? regulatoryInfo.groupMotherName
                            : gT(t, '.value-entry.value.missing-info')
                        }
                      />
                    </DataPanel>
                  </Module.DataColumn>
                  <Module.DataColumn>
                    <DataPanel
                      title={gT(t, '.data-panel-title.board-members')}
                      scroll
                      maxHeight={488}
                    >
                      <DataPanel.Subtitle
                        title={gT(t, '.data-panel-subtitle.active')}
                        green
                      />
                      {activeDirectors.length === 0 ? (
                        <DataPanel.ValueEntry
                          label={gT(
                            t,
                            '.value-entry.label.no-active-directors'
                          )}
                        />
                      ) : (
                        activeDirectors.map((director) => {
                          return (
                            <DataPanel.DoubleValueEntry
                              key={director.id}
                              title={director.name}
                              subTitle={director.function}
                              valueOne={getDateYMD(director.appointmentDate)}
                              valueTwo={getDateYMD(director.exitingDate)}
                              // eslint-disable-next-line prettier/prettier
                              valueOneName={gT(
                                t,
                                '.value-entry.value-name.appointment-date'
                              )}
                              // eslint-disable-next-line prettier/prettier
                              valueTwoName={gT(
                                t,
                                '.value-entry.value-name.exiting-date'
                              )}
                            />
                          );
                        })
                      )}
                      <DataPanel.Subtitle
                        title={gT(t, '.data-panel-subtitle.inactive')}
                        red
                      />
                      {inactiveDirectors.length !== 0 ? (
                        inactiveDirectors.map((director) => {
                          return (
                            <DataPanel.DoubleValueEntry
                              key={director.id}
                              title={director.name}
                              subTitle={director.function}
                              valueOne={getDateYMD(director.appointmentDate)}
                              valueTwo={getDateYMD(director.exitingDate)}
                              // eslint-disable-next-line prettier/prettier
                              valueOneName={gT(
                                t,
                                '.value-entry.value-name.appointment-date'
                              )}
                              // eslint-disable-next-line prettier/prettier
                              valueTwoName={gT(
                                t,
                                '.value-entry.value-name.exiting-date'
                              )}
                            />
                          );
                        })
                      ) : (
                        <DataPanel.ValueEntry
                          label={gT(
                            t,
                            '.value-entry.label.no-active-directors'
                          )}
                        />
                      )}
                    </DataPanel>
                  </Module.DataColumn>
                  <Module.DataColumn>
                    <DataPanel
                      title={gT(t, '.data-panel-title.regulatory-requirements')}
                    >
                      <DataPanel.ValueEntry
                        label={gT(t, '.value-entry.label.f-tax')}
                        value={
                          regulatoryInfo
                            ? regulatoryInfo.fTax === true
                              ? gsT(t, '.labels.yes')
                              : regulatoryInfo.fTaxDeregistrationReason !== null
                              ? `${gT(
                                  t,
                                  '.value-entry.value.prefix-deregistered'
                                )} ${regulatoryInfo.fTaxDeregistrationReason}`
                              : gsT(t, '.labels.no')
                            : gT(t, '.value-entry.value.missing-info')
                        }
                        valueType=""
                      />
                      <DataPanel.ValueEntry
                        label={gT(t, '.value-entry.label.vat-registration')}
                        value={
                          regulatoryInfo
                            ? regulatoryInfo.moms === true
                              ? gsT(t, '.labels.yes')
                              : gsT(t, '.labels.no')
                            : gT(t, '.value-entry.value.missing-info')
                        }
                        valueType=""
                      />
                      <DataPanel.ValueEntry
                        label={gT(t, '.value-entry.label.employer-register')}
                        value={
                          regulatoryInfo
                            ? regulatoryInfo.empTax === true
                              ? gsT(t, '.labels.yes')
                              : gsT(t, '.labels.no')
                            : gT(t, '.value-entry.value.missing-info')
                        }
                      />
                      <DataPanel.ValueEntry
                        label={gT(t, '.value-entry.label.auditors-reservation')}
                        value={
                          regulatoryInfo
                            ? regulatoryInfo.auditorReservation === true
                              ? gsT(t, '.labels.yes')
                              : gsT(t, '.labels.no')
                            : gT(t, '.value-entry.value.missing-info')
                        }
                      />
                    </DataPanel>
                    <DataPanel title={gT(t, '.data-panel-title.skv')}>
                      <DataPanel.ValueEntry
                        label={gT(t, '.value-entry.label.tax-balance')}
                        value={
                          client.skvConnected && client.taxBalance != null
                            ? client.taxBalance
                            : gT(t, '.value-entry.value.not-connected')
                        }
                        valueType={
                          client.skvConnected && client.taxBalance != null
                            ? 'currency'
                            : ''
                        }
                      />
                    </DataPanel>
                  </Module.DataColumn>
                </Module.DataSection>
                <Module.DataSection>
                  <Module.DataColumn>
                    <DataPanel
                      title={gT(
                        t,
                        '.data-panel-title.paid-social-security-contributions'
                      )}
                      scroll
                      maxHeight={410}
                    >
                      {sortedPayrollData.map((data) => {
                        return (
                          <DataPanel.DoubleValueEntry
                            key={data.id}
                            title={`${data.fee} kr`}
                            valueOne={getDateYMD(data.payrollDate)}
                            valueTwo={getDateYMD(data.registerDate)}
                            valueOneName={gsT(t, '.labels.date')}
                            valueTwoName={gT(
                              t,
                              '.value-entry.value-name.registered'
                            )}
                          />
                        );
                      })}
                    </DataPanel>
                  </Module.DataColumn>
                  <DataPanel
                    title={gT(t, '.data-panel-title.pep-aml-screening')}
                    icon={faCircleQuestion}
                    iconHoverContent={
                      <p>
                        {gT(t, '.data-panel-hover-content.pep-aml-screening.0')}
                        <br />
                        <br />
                        {gT(t, '.data-panel-hover-content.pep-aml-screening.1')}
                        <br />
                        <br />
                        {gT(t, '.data-panel-hover-content.pep-aml-screening.2')}
                      </p>
                    }
                    minHeight={128}
                    maxHeight={410}
                    scroll
                    footer={
                      <>
                        <XpektorButton
                          content={gT(t, '.xpektor-btn.request-screening')}
                          onClick={() => requestScreening()}
                          loading={!screeningsFetched}
                          disabled={
                            screeningInProgress ||
                            !screeningsFetched ||
                            isImpersonating ||
                            !authState.user ||
                            authState.user.role < 1
                          }
                        />
                        <p>
                          <i>{gT(t, '.data-panel-footer.pep-aml-screening')}</i>
                        </p>
                      </>
                    }
                  >
                    {screenings.length > 0 ? (
                      screenings
                        .sort(
                          (a, b) =>
                            new Date(b.dateStarted) - new Date(a.dateStarted)
                        )
                        .map((screening) => {
                          return (
                            <DataPanel.ScreeningEntry
                              key={screening.id}
                              entry={screening}
                              onResultClick={(result) => {
                                setSelectedResult(result);
                                setShowScreeningResultModal(true);
                              }}
                              t={t}
                            />
                          );
                        })
                    ) : (
                      <DataPanel.ValueEntry
                        label={gT(t, '.value-entry.label.no-screenings')}
                      />
                    )}
                  </DataPanel>
                  <Module.DataColumn>
                    <DataPanel
                      title={gT(t, '.data-panel-title.ongoing-matters')}
                      scroll
                      maxHeight={410}
                    >
                      {ongoingMatters.length === 0 ? (
                        <DataPanel.ValueEntry
                          label={gT(t, '.value-entry.label.no-data')}
                        />
                      ) : (
                        ongoingMatters.map((data) => {
                          return (
                            <DataPanel.DoubleValueEntry
                              key={data.id}
                              title={data.matterText}
                              subTitle={data.caseNumber}
                              valueOne={getDateYMD(data.dateAdded)}
                              valueOneName={`${gsT(t, '.labels.date')}:`}
                            />
                          );
                        })
                      )}
                    </DataPanel>
                  </Module.DataColumn>
                </Module.DataSection>
              </div>
            </XpektorSlider.Slide>
            <XpektorSlider.Slide hidden={regulatoryTab !== 1}>
              <KYCView
                kycStatus={kycStatus}
                onCreateClick={() => createNewKYCProcess()}
                onCancelClick={() => cancelKYCSubmission()}
                onResultClick={(result) => {
                  setSelectedResult(result);
                  setShowScreeningResultModal(true);
                }}
                isImpersonating={isImpersonating}
                t={t}
              />
            </XpektorSlider.Slide>
            <XpektorSlider.Slide hidden={regulatoryTab !== 2}>
              <CrimeRecordView
                clientId={client.id}
                isImpersonating={isImpersonating}
                t={t}
              />
            </XpektorSlider.Slide>
          </XpektorSlider>
        </>
      )}
      {showScreeningResultModal && (
        <ScreeningResultModal
          handleClose={() => {
            setShowScreeningResultModal(false);
            setSelectedResult({});
          }}
          result={selectedResult}
          t={t}
        />
      )}
    </Module>
  );
}
